import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Spinner } from 'reactstrap';
import AssetStatusBadge from '../Badges/AssetStatusBadge';

const ALL_COLUMNS = ['assetName', 'assetId', 'serialNumber', 'status', 'location'];

const AssetListTable = ({
    data = [],
    excludeColumns = [],
    onAssetClick,
    onLocationClick,
    customEmptyState,
    loading = false,
    loadingMessage,
    emptyMessage,
    emptySubMessage,
    emptyIcon = 'bx bx-package',
}) => {
    const { t } = useTranslation();

    const columns = ALL_COLUMNS.filter(col => !excludeColumns.includes(col));

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" size="lg" />
                <p className="text-muted mt-3">
                    {loadingMessage || t('shared.loading_assets', 'Loading assets...')}
                </p>
            </div>
        );
    }

    if (data.length === 0) {
        if (customEmptyState) return customEmptyState;

        return (
            <div className="text-center py-5">
                <div className="mb-3">
                    <i className={`${emptyIcon} text-muted`} style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-muted">
                    {emptyMessage || t('shared.no_assets_found', 'No assets found')}
                </h5>
                {emptySubMessage && (
                    <p className="text-muted small mb-0">{emptySubMessage}</p>
                )}
            </div>
        );
    }

    const getName = (asset) => asset.assetName || asset.name || '-';
    const getId = (asset) => asset.assetNumber || asset.assetId || '-';
    const getSerial = (asset) => asset.serialNumber || '-';
    const getLocation = (asset) => asset.currentLocation || '-';

    const columnConfig = {
        assetName: {
            header: t('shared.name', 'Name'),
            render: (asset) => {
                const name = getName(asset);
                if (onAssetClick) {
                    return (
                        <span
                            className="fw-semibold text-primary cursor-pointer hover-underline text-truncate d-inline-block"
                            onClick={() => onAssetClick(asset)}
                            style={{ cursor: 'pointer', maxWidth: '200px' }}
                            title={name}
                        >
                            {name}
                        </span>
                    );
                }
                return <span className="fw-medium">{name}</span>;
            },
        },
        assetId: {
            header: t('shared.asset_id', 'Asset ID'),
            render: (asset) => <span className="text-muted">{getId(asset)}</span>,
        },
        serialNumber: {
            header: t('shared.serial_number', 'Serial Number'),
            render: (asset) => <span className="text-muted">{getSerial(asset)}</span>,
        },
        status: {
            header: t('shared.asset_status', 'Asset Status'),
            render: (asset) => <AssetStatusBadge status={asset.assetStatus} />,
        },
        location: {
            header: t('shared.location', 'Location'),
            render: (asset) => {
                const loc = getLocation(asset);
                if (onLocationClick && loc !== '-') {
                    return (
                        <span
                            className="text-primary cursor-pointer hover-underline"
                            onClick={() => onLocationClick(asset)}
                            style={{ cursor: 'pointer' }}
                        >
                            {loc}
                        </span>
                    );
                }
                return <span className="text-muted">{loc}</span>;
            },
        },
    };

    return (
        <div className="table-responsive">
            <table className="table table-hover mb-0">
                <thead className="table-light">
                    <tr>
                        <th>#</th>
                        {columns.map(col => (
                            <th key={col}>{columnConfig[col].header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <AnimatePresence>
                        {data.map((asset, index) => (
                            <motion.tr
                                key={asset.id || `asset-${index}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.5) }}
                            >
                                <td>{index + 1}</td>
                                {columns.map(col => (
                                    <td key={col}>{columnConfig[col].render(asset)}</td>
                                ))}
                            </motion.tr>
                        ))}
                    </AnimatePresence>
                </tbody>
            </table>
        </div>
    );
};

export default AssetListTable;
